(function () {
  'use strict';
  if (window.MWStorefrontContent) return;
  const CONTRACT = 'mixwave.storefront-content';
  const HASH = /^[a-f0-9]{64}$/;
  const KEY = /^(artist|quote|quoteCollection|demo|audioSet|placement):[a-z0-9][a-z0-9_-]{0,79}$/i;
  const DESTINATION = /^(?:gid:\/\/shopify\/Product\/[1-9][0-9]*|page:artists)$/;
  const FIELDS = ['schemaVersion', 'contract', 'revision', 'coherent', 'destination', 'managed', 'placementKey', 'includesArtists', 'includesTestimonials', 'includesAudio', 'records'];
  const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
  const fail = () => { throw new Error('Published storefront content is unavailable or contains inconsistent projections.'); };

  function canonical(value, depth = 0) {
    if (depth > 12) fail();
    if (value === null || typeof value === 'boolean' || typeof value === 'string') return JSON.stringify(value);
    if (typeof value === 'number') { if (!Number.isFinite(value)) fail(); return JSON.stringify(value); }
    if (typeof value !== 'object') fail();
    if (Array.isArray(value)) {
      if (value.length > 10000 || Reflect.ownKeys(value).length !== value.length + 1) fail();
      const parts = [];
      for (let i = 0; i < value.length; i++) {
        const descriptor = Object.getOwnPropertyDescriptor(value, String(i));
        if (!descriptor || !descriptor.enumerable || !own(descriptor, 'value')) fail();
        parts.push(canonical(descriptor.value, depth + 1));
      }
      return `[${parts.join(',')}]`;
    }
    if (![Object.prototype, null].includes(Object.getPrototypeOf(value))) fail();
    const keys = Reflect.ownKeys(value);
    if (keys.some(key => typeof key !== 'string')) fail();
    return `{${keys.sort().map(key => {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor.enumerable || !own(descriptor, 'value')) fail();
      return `${JSON.stringify(key)}:${canonical(descriptor.value, depth + 1)}`;
    }).join(',')}}`;
  }

  function fromProjections(projections) {
    if (!Array.isArray(projections) || projections.length > 1000) fail();
    if (!projections.length) return Object.freeze({ status: 'unconfigured', revision: null, content: null, includesArtists: false, includesTestimonials: false, includesAudio: false, destinations: Object.freeze([]) });
    const records = new Map(), serialized = new Map(), destinations = new Map();
    let revision = null, includesArtists = false, includesTestimonials = false, includesAudio = false;
    for (const projection of projections) {
      canonical(projection);
      if (!projection || typeof projection !== 'object' || Array.isArray(projection) || Object.keys(projection).length !== FIELDS.length || FIELDS.some(field => !own(projection, field))) fail();
      if (projection.schemaVersion !== 1 || projection.contract !== CONTRACT || projection.coherent !== true || typeof projection.revision !== 'string' || !HASH.test(projection.revision)
        || typeof projection.managed !== 'boolean' || typeof projection.includesArtists !== 'boolean' || typeof projection.includesTestimonials !== 'boolean' || typeof projection.includesAudio !== 'boolean' || (projection.includesTestimonials && !projection.includesArtists) || !Array.isArray(projection.records) || projection.records.length > 10000
        || typeof projection.destination !== 'string' || (projection.destination !== '' && (!DESTINATION.test(projection.destination) || projection.destination.length > 128))) fail();
      if (revision !== null && revision !== projection.revision) fail(); revision = projection.revision;
      if (projection.managed) {
        if (!projection.destination || typeof projection.placementKey !== 'string' || !projection.placementKey.startsWith('placement:') || !KEY.test(projection.placementKey)) fail();
      } else if (projection.placementKey !== null) fail();
      if (destinations.has(projection.destination) && destinations.get(projection.destination) !== projection.placementKey) fail();
      destinations.set(projection.destination, projection.placementKey);
      includesArtists ||= projection.includesArtists; includesTestimonials ||= projection.includesTestimonials; includesAudio ||= projection.includesAudio;
      let foundPlacement = false;
      const projectionKeys = new Set();
      for (const record of projection.records) {
        const key = `${record?.kind}:${record?.id}`;
        if (!KEY.test(key) || projectionKeys.has(key)) fail(); projectionKeys.add(key);
        if (record.kind === 'placement') {
          const destination = record.destination?.type === 'product' ? record.destination.productId : record.destination?.type === 'page' ? `page:${record.destination.key}` : null;
          if (!projection.managed || key !== projection.placementKey || destination !== projection.destination || foundPlacement) fail();
          foundPlacement = true;
        }
        if (!projection.managed && !(projection.includesArtists && record.kind === 'artist') && !(projection.includesTestimonials && ['quote', 'quoteCollection'].includes(record.kind)) && !(projection.includesAudio && ['demo', 'audioSet'].includes(record.kind))) fail();
        const value = canonical(record);
        if (serialized.has(key) && serialized.get(key) !== value) fail();
        if (!records.has(key)) { records.set(key, record); serialized.set(key, value); }
        if (records.size > 10000) fail();
      }
      if (foundPlacement !== projection.managed) fail();
    }
    if (!window.MWStorefrontContentRecords?.create) fail();
    const content = window.MWStorefrontContentRecords.create([...records.values()]);
    return Object.freeze({ status: 'ready', revision, content, includesArtists, includesTestimonials, includesAudio, destinations: Object.freeze([...destinations.keys()].filter(Boolean)) });
  }

  let state = Object.freeze({ status: 'pending', revision: null, content: null, includesArtists: false, includesTestimonials: false, includesAudio: false, destinations: Object.freeze([]) });
  let ready, settledSignature = null, observed = false, refreshQueued = false;
  function notifyState(force = false) {
    const signature = JSON.stringify([state.status, state.revision, state.destinations, state.includesArtists, state.includesTestimonials, state.includesAudio]);
    if (signature === settledSignature && !force) return;
    settledSignature = signature;
    if (typeof document.dispatchEvent === 'function' && typeof window.CustomEvent === 'function') {
      document.dispatchEvent(new window.CustomEvent(state.status === 'error' ? 'mw:content-error' : 'mw:content-ready', {
        detail: Object.freeze({ status: state.status, revision: state.revision, destinations: state.destinations, includesArtists: state.includesArtists, includesTestimonials: state.includesTestimonials, includesAudio: state.includesAudio }),
      }));
    }
  }
  function observeProjections() {
    if (observed || !document.documentElement || typeof window.MutationObserver !== 'function') return;
    observed = true;
    const isProjection = node => Boolean(node?.matches?.('script[data-mw-storefront-content]') || node?.querySelector?.('script[data-mw-storefront-content]'));
    new window.MutationObserver(changes => {
      const changed = changes.some(change => change.type === 'characterData'
        ? isProjection(change.target.parentElement)
        : Boolean(change.target?.matches?.('script[data-mw-storefront-content]')) || [...change.addedNodes, ...change.removedNodes].some(isProjection));
      if (!changed || refreshQueued) return;
      refreshQueued = true;
      Promise.resolve().then(() => { refreshQueued = false; refresh({ notify: true }); });
    }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  }
  function refresh({ notify = false } = {}) {
    state = Object.freeze({ ...state, status: 'pending' });
    ready = Promise.resolve().then(() => {
      const nodes = [...document.querySelectorAll('script[data-mw-storefront-content]')];
      if (!nodes.length && document.documentElement?.hasAttribute?.('data-mw-content-enabled')) fail();
      const projections = nodes.map(node => {
        if (node.type !== 'application/json' || node.textContent.length > 16 * 1024 * 1024) fail();
        return JSON.parse(node.textContent);
      });
      state = fromProjections(projections);
      notifyState(notify);
      return state.content;
    }).catch(error => {
      state = Object.freeze({ status: 'error', revision: null, content: null, includesArtists: false, includesTestimonials: false, includesAudio: false, destinations: Object.freeze([]), error: 'Published storefront content could not be loaded.' });
      notifyState(notify);
      throw error;
    });
    ready.catch(() => {});
    return ready;
  }
  const api = { fromProjections, refresh, getState: () => state };
  Object.defineProperties(api, {
    ready: { enumerable: true, get: () => ready },
    available: { enumerable: true, get: () => state.status === 'ready' },
    failed: { enumerable: true, get: () => state.status === 'error' },
  });
  for (const method of ['productAudio', 'productFeatureAudio', 'cardAudio', 'productQuotes', 'gallery', 'quote', 'quoteCollection', 'record', 'all']) {
    api[method] = (...args) => { if (state.status !== 'ready' || !state.content) fail(); return state.content[method](...args); };
  }
  window.MWStorefrontContent = Object.freeze(api);
  if (document.readyState === 'loading') {
    ready = new Promise((resolve, reject) => {
      document.addEventListener('DOMContentLoaded', () => { observeProjections(); refresh().then(resolve, reject); }, { once: true });
    });
    ready.catch(() => {});
  } else { observeProjections(); refresh(); }
})();
